import React, { useState } from 'react';
import { FileText, CheckCircle, Mail, ArrowRight, Scale, BookOpen, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreeGuide = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/lead-capture`, { email });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const chapters = [
    'What a Lawsuit Is',
    'When a Lawsuit Is Appropriate',
    'Common Types of Civil Lawsuits',
    'Jurisdiction and Venue',
    'Elements Required to File a Claim',
    'Preparing a Complaint',
    'Evidence and Documentation',
    'Understanding Civil Procedure',
    'Service of Process',
    'What Happens After Filing',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-block bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                Free Download
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight" data-testid="free-guide-title">
                How, When, and Why to File a Lawsuit
              </h1>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                A practical legal guide covering the entire civil litigation process — from understanding your rights to filing a complaint and beyond.
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                <span className="flex items-center gap-1"><Scale size={14} /> Legal Citations</span>
                <span className="flex items-center gap-1"><BookOpen size={14} /> 13+ Chapters</span>
                <span className="flex items-center gap-1"><FileText size={14} /> PDF Format</span>
              </div>
            </div>

            {/* PDF Visual */}
            <div className="flex justify-center">
              <div className="bg-white rounded-xl shadow-2xl p-8 w-64 text-center transform rotate-2 hover:rotate-0 transition-transform">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-gray-900 font-bold text-sm mb-1">How to File a Lawsuit</h3>
                <p className="text-gray-500 text-xs mb-3">A Practical Legal Guide</p>
                <div className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full inline-block">
                  100% FREE
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Capture + Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-5 gap-10">
          {/* Left: Table of Contents */}
          <div className="md:col-span-3">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What You'll Learn</h2>
            <div className="space-y-3">
              {chapters.map((ch, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                  <div className="w-8 h-8 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  <span className="text-gray-800 font-medium text-sm pt-1">{ch}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Also Includes:</h3>
              <ul className="space-y-2">
                {['Federal Rules of Civil Procedure references', 'Major U.S. case law citations', 'Additional legal resources directory', 'About Paperwork Depot & guide catalog'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Email Form */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sticky top-20" data-testid="email-capture-card">
              {status !== 'success' ? (
                <>
                  <div className="text-center mb-5">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FileText className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Get Your Free Guide</h3>
                    <p className="text-sm text-gray-600 mt-1">Enter your email — we'll send the guide to your inbox</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                        required
                        data-testid="email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-5 text-base hover:from-green-600 hover:to-emerald-700 shadow-lg"
                      disabled={status === 'loading'}
                      data-testid="download-free-guide-btn"
                    >
                      {status === 'loading' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Mail size={18} className="mr-2" />
                          Send Me The Free Guide
                        </>
                      )}
                    </Button>
                  </form>

                  {status === 'error' && (
                    <p className="text-red-600 text-sm text-center mt-3" data-testid="capture-error">Something went wrong. Please try again.</p>
                  )}

                  <p className="text-xs text-gray-400 text-center mt-3">No spam. Unsubscribe anytime.</p>
                </>
              ) : (
                <div className="text-center" data-testid="download-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Check Your Email!</h3>
                  <p className="text-sm text-gray-600 mb-4">We've sent your free guide to <strong>{email}</strong>. Check your inbox (and spam folder) for the download link.</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-left">
                    <p className="text-xs text-green-800 font-medium flex items-center gap-2">
                      <CheckCircle size={14} /> Email sent with your download link
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-3">Want more legal guides?</p>
                    <a
                      href="/shop"
                      className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-semibold text-sm"
                    >
                      Browse Our Full Library <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FreeGuide;
